import { PackageOpen, ShieldOff, TriangleAlert } from 'lucide-react'
import { kit, transport } from './strings'
import './States.css'

/**
 * The four things a data panel can show instead of data. `LoadingOverlay` stays the
 * full-screen curtain (the legacy `#loading`); these are the in-panel versions.
 */

/**
 * Nothing to show. The default glyph is an OPENED box rather than an inbox: an
 * inbox is a place messages arrive, which says the wrong thing under a table of
 * training programmes — an opened, empty container says "we looked, there is
 * nothing in it", which is what this panel means every time it appears.
 */
export function EmptyState({ icon: Icon = PackageOpen, title, description, action }) {
  return (
    <div className="state state--empty" data-testid="state-empty">
      {/* Three ghost rows behind the emblem: the shape of the list that would be
          here. It is what turns a bare message into an empty TABLE, and it costs
          no markup at the call sites. */}
      <span className="state__ghost" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <span className="state__icon" aria-hidden="true">
        <Icon size={26} strokeWidth={1.5} />
      </span>
      <p className="state__title">
        <bdi>{title}</bdi>
      </p>
      {description && (
        <p className="state__description">
          <bdi>{description}</bdi>
        </p>
      )}
      {action && <div className="state__action">{action}</div>}
    </div>
  )
}

/**
 * For panels with no shape to hold — a chart, a detail card. A LIST should use
 * `SkeletonRows` instead: it keeps the table's own shape, so the data arriving
 * is a fill rather than the page jumping from an empty panel to a full one.
 *
 * @param {boolean} [inline]  drop the block padding, for a cell or a button
 */
export function LoadingState({ label = kit.loading, inline = false }) {
  return (
    <div
      className={`state state--loading${inline ? ' state--inline' : ''}`}
      data-testid="state-loading"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="state__spinner" aria-hidden="true" />
      {label ? (
        <p className="state__description">
          <bdi>{label}</bdi>
        </p>
      ) : null}
    </div>
  )
}

/**
 * A 403, told apart from a failure.
 *
 * `ClientApp/CLAUDE.md` asks every screen for four states and this was the missing one:
 * `ApiError.denied` has been set on every 403 since `api.js` was written, the wording has
 * existed in both languages as `transport.denied`, and nothing ever rendered either.
 * A refused screen showed "something went wrong".
 *
 * The distinction is not cosmetic. "Something went wrong" invites a retry; "you do not have
 * permission" invites asking an administrator. This product has fourteen manager roles and a
 * licence gate, so a 403 is an ORDINARY outcome here, not an exceptional one — and sending
 * people to retry a screen their role will never open wastes their time and the
 * administrator's. No `action` slot for that reason: there is nothing for the user to press.
 */
export function DeniedState({ title = transport.denied, description }) {
  /* When the server sends no detail of its own, `api.js` falls back to `transport.denied` —
     the same sentence this uses as its title. Printing it twice reads as a stutter. */
  const detail = description && description !== title ? description : undefined

  return (
    <div className="state state--denied" data-testid="state-denied" role="alert">
      <span className="state__icon state__icon--denied" aria-hidden="true">
        <ShieldOff size={24} strokeWidth={1.5} />
      </span>
      <p className="state__title">
        <bdi>{title}</bdi>
      </p>
      {detail && (
        <p className="state__description">
          <bdi>{detail}</bdi>
        </p>
      )}
    </div>
  )
}

export function ErrorState({ title, description, action }) {
  return (
    <div className="state state--error" data-testid="state-error" role="alert">
      <span className="state__icon state__icon--error" aria-hidden="true">
        <TriangleAlert size={24} strokeWidth={1.5} />
      </span>
      <p className="state__title">
        <bdi>{title}</bdi>
      </p>
      {description && (
        <p className="state__description">
          <bdi>{description}</bdi>
        </p>
      )}
      {action && <div className="state__action">{action}</div>}
    </div>
  )
}
